import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { buildSignalRUrl } from '../lib/config';
import type { NotificationResponse } from '../types/notifications';

// SignalR event data types
interface TicketUpdatedData {
  ticketId?: string;
  TicketId?: string;
  changes?: string[];
  updatedBy?: string;
}

interface CommentAddedData {
  ticketId?: string;
  TicketId?: string;
  commentId?: string;
  CommentId?: string;
  author?: string;
}

interface CommentAttachmentAddedData {
  ticketId?: string;
  TicketId?: string;
  FileName?: string;
  fileName?: string;
}

interface UserTicketData {
  ticketId?: string;
  TicketId?: string;
  userId?: string;
  UserId?: string;
  userName?: string;
  UserName?: string;
}

interface TicketCreatedData {
  TicketData?: {
    TicketNumber?: number;
    Id?: string;
  };
}

interface TicketActivityData {
  ticketId?: string;
  TicketId?: string;
  activityType?: string;
  message?: string;
}

interface NotificationData {
  id?: string;
  message?: string;
  type?: string;
  timestamp?: string;
}

interface ParticipantsChangedData {
  ticketId: string;
  organizationId: string;
  changeType: 'added' | 'removed';
  participant: {
    participantId?: string;
    customerId: string;
    customerName: string;
    customerEmail: string;
  };
  changedAt: string;
}

interface TicketMergedData {
  sourceTicketId: string;
  sourceTicketNumber: number;
  targetTicketId: string;
  targetTicketNumber: number;
  organizationId: string;
  mergedAt: string;
}

interface FollowersChangedData {
  ticketId: string;
  organizationId: string;
  changeType: 'added' | 'removed';
  follower: {
    userId: string;
    userName: string;
  };
  changedAt: string;
}

interface NotificationsMarkedAsReadData {
  newUnreadCount: number;
  markedAt: string;
}

// Sent by SessionService when a session is revoked; organizationId names the one organization affected (null = all)
interface SessionRevokedData {
  organizationId?: string | null;
}

interface SignalREventHandlers {
  // Ticket Hub Events
  TicketUpdated: (data: TicketUpdatedData) => void;
  CommentAdded: (data: CommentAddedData) => void;
  CommentAttachmentAdded: (data: CommentAttachmentAddedData) => void;
  ParticipantsChanged: (data: ParticipantsChangedData) => void;
  FollowersChanged: (data: FollowersChangedData) => void;
  TicketMerged: (data: TicketMergedData) => void;
  TicketMergeUpdate: (data: TicketMergedData) => void;
  UserJoinedTicket: (data: UserTicketData) => void;
  UserLeftTicket: (data: UserTicketData) => void;
  UserStartedTyping: (data: UserTicketData) => void;
  UserStoppedTyping: (data: UserTicketData) => void;
  UserViewingTicket: (data: UserTicketData) => void;
  TicketNotification: (data: NotificationData) => void;

  // Notification Hub Events
  TicketCreated: (data: TicketCreatedData) => void;
  TicketUpdate: (data: TicketUpdatedData) => void;
  TicketActivity: (data: TicketActivityData) => void;
  UserNotification: (data: NotificationData) => void;
  OrganizationNotification: (data: NotificationData) => void;
  RoleNotification: (data: NotificationData) => void;

  // In-App Notification Events
  InAppNotification: (data: NotificationResponse) => void;
  NotificationsMarkedAsRead: (data: NotificationsMarkedAsReadData) => void;
  SessionRevoked: (data: SessionRevokedData) => void;
}

export class SignalRService {
  private ticketConnection: HubConnection | null = null;
  private notificationConnection: HubConnection | null = null;
  private accessToken: string = '';
  // Multi-handler architecture: allows multiple components to listen to the same event
  private eventHandlers: Map<keyof SignalREventHandlers, Set<Function>> = new Map();
  private connectionStateCallbacks: Set<() => void> = new Set();
  private isInitialized = false;
  private isInitializing = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  public async initialize(accessToken: string): Promise<void> {
    // Prevent concurrent initialization - this fixes React Strict Mode race condition
    if (this.isInitializing) {
      return;
    }

    // If already connected with same token, no need to reinitialize
    if (this.isInitialized && this.accessToken === accessToken) {
      return;
    }

    if (this.isInitialized) {
      await this.disconnect();
    }

    this.isInitializing = true;

    try {
      this.accessToken = accessToken;

      // Initialize Ticket Hub connection
      this.ticketConnection = new HubConnectionBuilder()
        .withUrl(buildSignalRUrl('/ticketHub'), {
          accessTokenFactory: () => this.accessToken,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.previousRetryCount < 5) {
              return Math.random() * 10000;
            } else {
              return null; // Stop retrying after 5 attempts
            }
          }
        })
        .configureLogging(LogLevel.Information)
        .build();

      // Initialize Notification Hub connection
      this.notificationConnection = new HubConnectionBuilder()
        .withUrl(buildSignalRUrl('/notificationHub'), {
          accessTokenFactory: () => this.accessToken,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.previousRetryCount < 5) {
              return Math.random() * 10000;
            } else {
              return null; // Stop retrying after 5 attempts
            }
          }
        })
        .configureLogging(LogLevel.Information)
        .build();

      this.setupEventHandlers();
      await this.startConnections();
      this.isInitialized = true;
    } finally {
      this.isInitializing = false;
    }
  }

  private setupEventHandlers(): void {
    if (!this.ticketConnection || !this.notificationConnection) return;

    // Helper to dispatch to all registered handlers for an event
    const dispatch = <K extends keyof SignalREventHandlers>(event: K, data: Parameters<SignalREventHandlers[K]>[0]) => {
      this.eventHandlers.get(event)?.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`[SignalR] Error in ${event} handler:`, error);
        }
      });
    };

    // Ticket Hub Events
    this.ticketConnection.on('TicketUpdated', (data) => dispatch('TicketUpdated', data));
    this.ticketConnection.on('CommentAdded', (data) => dispatch('CommentAdded', data));
    this.ticketConnection.on('CommentAttachmentAdded', (data) => dispatch('CommentAttachmentAdded', data));
    this.ticketConnection.on('ParticipantsChanged', (data) => dispatch('ParticipantsChanged', data));
    this.ticketConnection.on('FollowersChanged', (data) => dispatch('FollowersChanged', data));
    this.ticketConnection.on('TicketMerged', (data) => dispatch('TicketMerged', data));
    this.ticketConnection.on('UserJoinedTicket', (data) => dispatch('UserJoinedTicket', data));
    this.ticketConnection.on('UserLeftTicket', (data) => dispatch('UserLeftTicket', data));
    this.ticketConnection.on('UserStartedTyping', (data) => dispatch('UserStartedTyping', data));
    this.ticketConnection.on('UserStoppedTyping', (data) => dispatch('UserStoppedTyping', data));
    this.ticketConnection.on('UserViewingTicket', (data) => dispatch('UserViewingTicket', data));
    this.ticketConnection.on('TicketNotification', (data) => dispatch('TicketNotification', data));

    // Notification Hub Events
    this.notificationConnection.on('TicketCreated', (data) => dispatch('TicketCreated', data));
    this.notificationConnection.on('TicketUpdate', (data) => dispatch('TicketUpdate', data));
    this.notificationConnection.on('TicketActivity', (data) => dispatch('TicketActivity', data));
    this.notificationConnection.on('TicketMergeUpdate', (data) => dispatch('TicketMergeUpdate', data));
    this.notificationConnection.on('UserNotification', (data) => dispatch('UserNotification', data));
    this.notificationConnection.on('OrganizationNotification', (data) => dispatch('OrganizationNotification', data));
    this.notificationConnection.on('RoleNotification', (data) => dispatch('RoleNotification', data));

    // In-App Notification Events
    this.notificationConnection.on('InAppNotification', (data) => dispatch('InAppNotification', data));
    this.notificationConnection.on('NotificationsMarkedAsRead', (data) => dispatch('NotificationsMarkedAsRead', data));
    // Server ended this session (access removed, account deactivated, password reset) - see SignalRContext
    this.notificationConnection.on('SessionRevoked', (data) => dispatch('SessionRevoked', data));

    // Setup connection state change listeners
    this.ticketConnection.onreconnected(() => this.notifyConnectionStateChange());
    this.ticketConnection.onclose(() => this.notifyConnectionStateChange());
    this.ticketConnection.onreconnecting(() => this.notifyConnectionStateChange());
    this.notificationConnection.onreconnected(() => this.notifyConnectionStateChange());
    this.notificationConnection.onclose(() => this.notifyConnectionStateChange());
    this.notificationConnection.onreconnecting(() => this.notifyConnectionStateChange());
  }

  private notifyConnectionStateChange(): void {
    this.connectionStateCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('[SignalR] Error in connection state callback:', error);
      }
    });
  }

  private async startConnections(): Promise<void> {
    if (!this.ticketConnection || !this.notificationConnection) {
      return;
    }

    try {
      await this.ticketConnection.start();
      await this.notificationConnection.start();
      this.reconnectAttempts = 0;
    } catch (error) {
      console.error('[SignalR] Connection error:', error);
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

      setTimeout(async () => {
        try {
          await this.startConnections();
        } catch {
          this.scheduleReconnect();
        }
      }, delay);
    }
  }

  /**
   * Register a handler for a SignalR event. Returns a cleanup function.
   * Multiple handlers can be registered for the same event.
   */
  public on<K extends keyof SignalREventHandlers>(
    event: K,
    handler: SignalREventHandlers[K]
  ): () => void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler as Function);

    // Return cleanup function
    return () => {
      this.eventHandlers.get(event)?.delete(handler as Function);
    };
  }

  /**
   * Remove a specific handler for an event.
   * @deprecated Use the cleanup function returned by on() instead
   */
  public off(event: keyof SignalREventHandlers, handler?: Function): void {
    if (handler) {
      this.eventHandlers.get(event)?.delete(handler);
    } else {
      // For backwards compatibility, clear all handlers for this event
      this.eventHandlers.delete(event);
    }
  }

  /**
   * Subscribe to connection state changes. Returns a cleanup function.
   */
  public onConnectionStateChange(callback: () => void): () => void {
    this.connectionStateCallbacks.add(callback);
    return () => {
      this.connectionStateCallbacks.delete(callback);
    };
  }

  public async disconnect(): Promise<void> {
    // Don't disconnect while initializing - this causes the negotiation error
    if (this.isInitializing) {
      return;
    }

    try {
      if (this.ticketConnection) {
        await this.ticketConnection.stop();
        this.ticketConnection = null;
      }

      if (this.notificationConnection) {
        await this.notificationConnection.stop();
        this.notificationConnection = null;
      }

      this.eventHandlers.clear();
      this.connectionStateCallbacks.clear();
      this.isInitialized = false;
    } catch {
      // Silently handle disconnect errors
    }
  }

  // Ticket Hub Methods
  public async joinTicket(ticketId: string): Promise<void> {
    if (this.ticketConnection?.state === 'Connected') {
      try {
        await this.ticketConnection.invoke('JoinTicket', ticketId);
      } catch (error) {
        console.error('Error joining ticket:', error);
      }
    }
  }

  public async leaveTicket(ticketId: string): Promise<void> {
    if (this.ticketConnection?.state === 'Connected') {
      try {
        await this.ticketConnection.invoke('LeaveTicket', ticketId);
      } catch (error) {
        console.error('Error leaving ticket:', error);
      }
    }
  }

  public async startTyping(ticketId: string): Promise<void> {
    if (this.ticketConnection?.state === 'Connected') {
      try {
        await this.ticketConnection.invoke('StartTyping', ticketId);
      } catch (error) {
        console.error('Error starting typing:', error);
      }
    }
  }

  public async stopTyping(ticketId: string): Promise<void> {
    if (this.ticketConnection?.state === 'Connected') {
      try {
        await this.ticketConnection.invoke('StopTyping', ticketId);
      } catch (error) {
        console.error('Error stopping typing:', error);
      }
    }
  }

  public async viewingTicket(ticketId: string): Promise<void> {
    if (this.ticketConnection?.state === 'Connected') {
      try {
        await this.ticketConnection.invoke('ViewingTicket', ticketId);
      } catch (error) {
        console.error('Error viewing ticket:', error);
      }
    }
  }

  // Connection State
  public get isConnected(): boolean {
    return this.ticketConnection?.state === 'Connected' && 
           this.notificationConnection?.state === 'Connected';
  }

  public get connectionState(): { ticket: string; notification: string } {
    return {
      ticket: this.ticketConnection?.state ?? 'Disconnected',
      notification: this.notificationConnection?.state ?? 'Disconnected'
    };
  }
}

// Singleton instance
export const signalRService = new SignalRService();